import { navigationSectionsConfig } from '@shared/config/navigation';
import { ChevronRight } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCurrentBreadcrumbLabel } from '@shared/contexts/BreadcrumbLabelContext';

interface BreadcrumbItem {
  label: string;
  path?: string;
}

// Map of route patterns to translation keys (without section prefix)
const pageLabelKeys: Record<string, string> = {
  '/dashboard': 'nav.dashboard',
  '/backup-jobs': 'nav.backupJobs',
  '/backups/configs': 'nav.backupConfigs',
  '/backups/quick': 'nav.quickBackup',
  '/schedules': 'nav.schedules',
  '/agents': 'nav.agents',
  '/agent-groups': 'nav.agentGroups',
  '/storage-breakdown': 'nav.storageBreakdown',
  '/storage-destinations': 'nav.storageDestinations',
  '/data-locations': 'nav.dataLocations',
  '/organizations': 'nav.organizations',
  '/team-members': 'nav.teamMembers',
  '/custom-roles': 'nav.customRoles',
  '/api-keys': 'nav.apiKeys',
  '/api-usage': 'nav.apiUsage',
  '/audit-logs': 'nav.auditLogs',
  '/system-logs': 'nav.systemLogs',
  '/log-retention-settings': 'nav.logRetention',
  '/data-breaches': 'nav.dataBreaches',
  '/data-export': 'nav.dataExport',
  '/compliance-reports': 'nav.complianceReports',
  '/notifications': 'nav.notifications',
  '/webhooks': 'nav.webhooks',
  '/metrics': 'nav.metrics',
  '/metrics-alerts': 'nav.metricsAlerts',
  '/smtp-settings': 'nav.smtpSettings',
  '/billing': 'nav.billingPlans',
  '/billing/success': 'nav.billingSuccess',
  '/invoices': 'nav.invoices',
  '/profile': 'nav.profile',
  '/change-password': 'nav.changePassword',
  '/settings': 'nav.settings',
  '/admin/dashboard': 'nav.adminDashboard',
  '/admin/analytics': 'nav.analytics',
  '/admin/health': 'nav.systemHealth',
  '/admin/settings': 'nav.systemSettings',
  '/admin/integrations': 'nav.integrations',
  '/admin/customers': 'nav.customers',
  '/admin/reports': 'nav.reports',
  '/admin/binaries': 'nav.agentBinaries',
  '/admin/plans': 'nav.planManagement',
};

// Map of parent paths for dynamic routes
const parentPaths: Record<string, string> = {
  '/backups/configs': '/backups/configs',
  '/agents': '/agents',
  '/organizations': '/organizations',
  '/team-members': '/team-members',
  '/admin/customers': '/admin/customers',
};

// Helper to get section title key for a path
const getSectionTitleKey = (pathname: string): string | null => {
  for (const section of navigationSectionsConfig) {
    for (const item of section.items) {
      // Exact match
      if (item.path === pathname) {
        return section.titleKey;
      }
      // Prefix match for dynamic routes (e.g., /agents/:id matches /agents/abc)
      if (pathname.startsWith(item.path + '/')) {
        return section.titleKey;
      }
    }
  }
  return null;
};

function getBreadcrumbs(
  pathname: string,
  t: (key: string) => string,
  currentPageLabel?: string
): BreadcrumbItem[] {
  // Dashboard root
  if (pathname === '/dashboard') {
    return [{ label: t('nav.dashboard') }];
  }

  if (pathname === '/data-export') {
    return [{ label: t('nav.profile'), path: '/profile' }, { label: t('nav.dataExport') }];
  }

  if (pathname === '/mfa/setup') {
    return [{ label: t('nav.profile'), path: '/profile' }, { label: t('nav.mfaSetup') }];
  }

  // Check for exact match
  if (pageLabelKeys[pathname]) {
    const sectionTitleKey = getSectionTitleKey(pathname);
    if (sectionTitleKey) {
      return [{ label: t(`nav.${sectionTitleKey}`) }, { label: t(pageLabelKeys[pathname]) }];
    }
  }

  // Backup configuration child routes end with an action segment, not the config ID.
  if (pathname.startsWith('/backups/configs/')) {
    const segments = pathname.split('/').filter(Boolean);
    const suffix = segments[3];
    const childLabelKeys: Record<string, string> = {
      edit: 'nav.backupConfigEdit',
      snapshots: 'nav.backupSnapshots',
      'alert-state': 'nav.backupAlertState',
    };
    return [
      { label: t(pageLabelKeys['/backups/configs']), path: '/backups/configs' },
      {
        label: childLabelKeys[suffix]
          ? t(childLabelKeys[suffix])
          : (segments[2] || '').slice(0, 8).toUpperCase(),
      },
    ];
  }

  // Check for dynamic routes (e.g., /backups/configs/:id)
  for (const [parentPath] of Object.entries(parentPaths)) {
    if (pathname.startsWith(parentPath + '/') && pathname !== parentPath) {
      const sectionTitleKey = getSectionTitleKey(parentPath);
      const id = pathname.split('/').pop() || '';
      return [
        { label: sectionTitleKey ? t(`nav.${sectionTitleKey}`) : '' },
        { label: t(pageLabelKeys[parentPath]), path: parentPath },
        { label: currentPageLabel || id.substring(0, 8).toUpperCase() },
      ];
    }
  }

  // Fallback: generate from path
  const segments = pathname.split('/').filter(Boolean);
  return segments.map((segment, index) => {
    const path = '/' + segments.slice(0, index + 1).join('/');
    return {
      label: segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' '),
      path: index === segments.length - 1 ? undefined : path,
    };
  });
}

export default function Breadcrumb() {
  const location = useLocation();
  const { t } = useTranslation('navigation');
  const currentPageLabel = useCurrentBreadcrumbLabel(location.pathname);
  const breadcrumbs = getBreadcrumbs(location.pathname, t, currentPageLabel);

  // Don't show breadcrumbs on dashboard
  if (location.pathname === '/dashboard' || breadcrumbs.length <= 1) {
    return null;
  }

  return (
    <nav
      className="flex min-w-0 max-w-full items-center overflow-hidden"
      aria-label={t('breadcrumbs.label')}
    >
      <ol className="flex min-w-0 max-w-full items-center space-x-1.5">
        {breadcrumbs.map((item, index) => (
          <li
            key={index}
            className={`flex items-center ${index === breadcrumbs.length - 1 ? 'min-w-0 flex-1' : 'shrink-0'}`}
          >
            {index > 0 && (
              <ChevronRight
                className="mx-1 h-3.5 w-3.5 shrink-0 text-slate-300 dark:text-slate-600"
                strokeWidth={3}
              />
            )}
            {item.path ? (
              <Link
                to={item.path}
                title={item.label}
                className={`min-w-0 text-xs font-bold text-text-subtle-light dark:text-text-subtle-dark hover:text-brand-600 dark:hover:text-brand-400 uppercase tracking-widest transition-colors ${index === breadcrumbs.length - 1 ? 'truncate' : 'whitespace-nowrap'}`}
              >
                {item.label}
              </Link>
            ) : (
              <span
                title={item.label}
                className={`min-w-0 text-xs font-black text-slate-900 dark:text-white uppercase tracking-widest ${index === breadcrumbs.length - 1 ? 'block truncate' : 'whitespace-nowrap'}`}
              >
                {item.label}
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}
